import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './Operations.css';

function CaseConversion() {
  const { sku } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [destinationBin, setDestinationBin] = useState(null);
  const [suggestedBins, setSuggestedBins] = useState(null);
  const [manualBinEntry, setManualBinEntry] = useState(false);
  const [manualBinCode, setManualBinCode] = useState('');
  const [conversionResult, setConversionResult] = useState(null);
  const [isValidCaseSku, setIsValidCaseSku] = useState(false);

  // Determine the case SKU from the provided SKU
  const getCaseSku = (inputSku) => {
    if (!inputSku) return null;

    // If it's already a case SKU (has '2' after prefix)
    if (inputSku.match(/^[A-Z]+2/i)) {
      return inputSku.toUpperCase();
    }

    // If it's a singles SKU (has '1' after prefix), convert to case
    if (inputSku.match(/^[A-Z]+1/i)) {
      return inputSku.replace(/^([A-Z]+)1/i, '$12').toUpperCase();
    }

    return null;
  };

  const caseSku = getCaseSku(sku);

  useEffect(() => {
    if (caseSku) {
      setIsValidCaseSku(true);
      fetchInventory();
      fetchSuggestedBins();
    } else {
      setIsValidCaseSku(false);
      setLoading(false);
    }
  }, [caseSku]);

  const fetchSuggestedBins = async () => {
    if (!caseSku) return;

    try {
      const response = await axios.get(`/api/ops/suggested-bins/${caseSku}`);
      if (response.data) {
        setSuggestedBins(response.data);
        // Pre-select the default bin if available
        if (response.data.defaultBin) {
          setDestinationBin(response.data.defaultBin);
        } else if (response.data.emptyBins && response.data.emptyBins.length > 0) {
          setDestinationBin(response.data.emptyBins[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch suggested bins:', error);
    }
  };

  const fetchInventory = async () => {
    if (!caseSku) return;

    setLoading(true);
    setConversionResult(null);
    try {
      const response = await axios.get(`/api/ops/inventory/${caseSku}`);
      if (response.data && response.data.length > 0) {
        setInventory(response.data);
        setSelectedBin(response.data[0].bin_id); // Select first bin by default
      } else {
        setInventory(null);
      }
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      setInventory(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!selectedBin) {
      alert('Please select a bin location');
      return;
    }

    const selectedBinData = inventory.find(inv => inv.bin_id === selectedBin);
    if (!selectedBinData) {
      alert('Invalid bin selection');
      return;
    }

    if (quantity > selectedBinData.quantity) {
      alert(`Cannot convert ${quantity} cases. Only ${selectedBinData.quantity} available in ${selectedBinData.bin_code}`);
      return;
    }

    setLoading(true);
    try {
      // Determine the actual destination bin
      let finalDestinationBin = destinationBin;
      if (manualBinEntry && manualBinCode.trim()) {
        // For manual entry, we'll send the bin code as the ID
        // The backend will need to look it up or create it
        finalDestinationBin = manualBinCode.trim();
      }

      const response = await axios.post('/api/ops/convert-case', {
        case_sku: caseSku,
        bin_id: selectedBin,
        destination_bin_id: finalDestinationBin,
        quantity: quantity,
        user: 'Mobile User'
      });

      if (response.data.success) {
        setConversionResult(response.data);
        // Refresh inventory
        await fetchInventory();
        setQuantity(1); // Reset quantity
      }
    } catch (error) {
      console.error('Conversion failed:', error);
      alert(error.response?.data?.error || 'Failed to convert case to singles');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ops-page">
      <div className="ops-page-header">
        <Link to={`/ops/${sku}`} className="back-button">← Back to Menu</Link>
        <h1>Case to Singles Conversion</h1>
        {caseSku && <h2 className="sku-label">Converting: {caseSku}</h2>}
      </div>

      <div className="conversion-form">
        {loading ? (
          <LoadingSpinner />
        ) : !isValidCaseSku ? (
          <div className="error-message">
            <h3>Invalid SKU for Conversion</h3>
            <p>SKU "{sku}" cannot be converted. Only case items (prefix 2) can be converted to singles.</p>
            <Link to={`/ops/${sku}`} className="primary-button">Back to Operations</Link>
          </div>
        ) : !inventory || inventory.length === 0 ? (
          <div className="no-data">
            <h3>No Inventory Found</h3>
            <p>No case inventory found for {caseSku}</p>
            <Link to={`/ops/${sku}`} className="primary-button">Back to Operations</Link>
          </div>
        ) : null}

        {inventory && (
          <>
            <div className="form-section">
              <label className="form-label">Select Bin Location</label>
              <div className="bin-selection">
                {inventory.map((inv) => (
                  <div
                    key={inv.bin_id}
                    className={`bin-option ${selectedBin === inv.bin_id ? 'selected' : ''}`}
                    onClick={() => setSelectedBin(inv.bin_id)}
                  >
                    <div className="bin-option-header">
                      <input
                        type="radio"
                        checked={selectedBin === inv.bin_id}
                        onChange={() => setSelectedBin(inv.bin_id)}
                        name="bin-selection"
                      />
                      <span className="bin-code">{inv.bin_code}</span>
                    </div>
                    <div className="bin-option-details">
                      <span>Available: {inv.quantity} cases</span>
                      <span>Zone {inv.zone} | Aisle {inv.aisle} | Shelf {inv.shelf}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-section">
              <label className="form-label">Number of Cases to Convert</label>
              <input
                type="number"
                min="1"
                max={inventory.find(inv => inv.bin_id === selectedBin)?.quantity || 1}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="form-control-large"
                disabled={loading}
              />
              <div className="conversion-preview">
                Converting {quantity} case{quantity > 1 ? 's' : ''} will create approximately {quantity * 6} singles
              </div>
            </div>

            <div className="form-section">
              <label className="form-label">Destination Bin for Singles</label>
              {suggestedBins ? (
                <div className="bin-selection">
                  {/* Bins with existing singles */}
                  {suggestedBins.binsWithItem && suggestedBins.binsWithItem.length > 0 && (
                    <>
                      <div className="bin-group-label">Bins with existing singles</div>
                      {suggestedBins.binsWithItem.map(bin => (
                        <div
                          key={bin.id}
                          className={`bin-option ${destinationBin === bin.id ? 'selected' : ''}`}
                          onClick={() => {
                            setDestinationBin(bin.id);
                            setManualBinEntry(false);
                            setManualBinCode('');
                          }}
                        >
                          <div className="bin-option-header">
                            <input
                              type="radio"
                              checked={destinationBin === bin.id}
                              onChange={() => setDestinationBin(bin.id)}
                              name="destination-bin-selection"
                            />
                            <span className="bin-code">{bin.name}</span>
                            <span className="bin-badge recommended">Recommended</span>
                          </div>
                          <div className="bin-option-details">
                            <span>Current: {bin.currentQty} singles</span>
                            <span>Available space</span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Preferred bins (configured for this item but currently empty) */}
                  {suggestedBins.preferredBins && suggestedBins.preferredBins.length > 0 && (
                    <>
                      <div className="bin-group-label">Preferred bins (empty)</div>
                      {suggestedBins.preferredBins.map(bin => (
                        <div
                          key={bin.id}
                          className={`bin-option ${destinationBin === bin.id ? 'selected' : ''}`}
                          onClick={() => {
                            setDestinationBin(bin.id);
                            setManualBinEntry(false);
                            setManualBinCode('');
                          }}
                        >
                          <div className="bin-option-header">
                            <input
                              type="radio"
                              checked={destinationBin === bin.id}
                              onChange={() => setDestinationBin(bin.id)}
                              name="destination-bin-selection"
                            />
                            <span className="bin-code">{bin.name}</span>
                            <span className="bin-badge preferred">Preferred</span>
                          </div>
                          <div className="bin-option-details">
                            <span>Configured for this item</span>
                            <span>Currently empty</span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Other options */}
                  <div className="bin-group-label">Other options</div>
                  <div
                    className={`bin-option ${destinationBin === selectedBin ? 'selected' : ''}`}
                    onClick={() => {
                      setDestinationBin(selectedBin);
                      setManualBinEntry(false);
                      setManualBinCode('');
                    }}
                  >
                    <div className="bin-option-header">
                      <input
                        type="radio"
                        checked={destinationBin === selectedBin}
                        onChange={() => setDestinationBin(selectedBin)}
                        name="destination-bin-selection"
                      />
                      <span className="bin-code">Same as source ({inventory.find(inv => inv.bin_id === selectedBin)?.bin_code})</span>
                    </div>
                    <div className="bin-option-details">
                      <span>Keep singles with cases</span>
                      <span>No bin transfer needed</span>
                    </div>
                  </div>

                  {/* Manual bin entry option */}
                  <div
                    className={`bin-option ${manualBinEntry ? 'selected' : ''}`}
                    onClick={() => {
                      setManualBinEntry(true);
                      setDestinationBin(null);
                    }}
                  >
                    <div className="bin-option-header">
                      <input
                        type="radio"
                        checked={manualBinEntry}
                        onChange={() => {
                          setManualBinEntry(true);
                          setDestinationBin(null);
                        }}
                        name="destination-bin-selection"
                      />
                      <span className="bin-code">Enter bin manually</span>
                    </div>
                    <div className="bin-option-details">
                      <span>Specify a custom bin location</span>
                    </div>
                  </div>

                  {/* Manual bin input field */}
                  {manualBinEntry && (
                    <div className="manual-bin-input">
                      <input
                        type="text"
                        placeholder="Enter bin code (e.g., B1, 9-1-A)"
                        value={manualBinCode}
                        onChange={(e) => setManualBinCode(e.target.value.toUpperCase())}
                        className="form-control"
                        disabled={loading}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="loading-inline">Loading bin suggestions...</div>
              )}
            </div>

            <button
              onClick={handleConvert}
              disabled={loading || !selectedBin || quantity < 1}
              className="convert-action-button"
            >
              {loading ? 'Converting...' : `Convert ${quantity} Case${quantity > 1 ? 's' : ''} to Singles`}
            </button>
          </>
        )}

        {conversionResult && (
          <div className="success-message">
            <h3>✓ Conversion Successful!</h3>
            <div className="result-details">
              <p>{conversionResult.message}</p>
              {conversionResult.details && (
                <ul>
                  <li>Cases converted: {conversionResult.details.casesConverted}</li>
                  <li>Singles created: {conversionResult.details.singlesCreated}</li>
                  <li>Singles SKU: {conversionResult.details.singlesSku}</li>
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseConversion;